import { Fragment, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useSignInClient } from '../hooks/auth/useSignInClient';
import { useContextStore } from '../hooks/context/useContextStore';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';
import FormField from './ui/FormField';

interface SessionEvictedModalProps {
  isOpen: boolean;
  onReauthenticated: () => void;
  onSignOut: () => void;
}

export default function SessionEvictedModal({
  isOpen,
  onReauthenticated,
  onSignOut,
}: SessionEvictedModalProps) {
  const { t } = useTranslation();
  useContextStore(); // Ensure we're within the context provider
  const { signIn } = useSignInClient();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email is locked to the current user — this modal only allows the SAME user to re-auth.
  // To switch accounts, the user clicks "Sign Out" which does a full signout + redirect.
  const storedUser = localStorage.getItem('clientUser');
  const parsedUser = storedUser
    ? (() => {
        try {
          return JSON.parse(storedUser);
        } catch {
          return null;
        }
      })()
    : null;
  const storedEmail = parsedUser?.email || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError(t('pleaseEnterYourPassword') || 'Please enter your password.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signIn({ email: storedEmail, password });

      if (result.success) {
        setPassword('');
        onReauthenticated();
      } else {
        setError(t('invalidPasswordPleaseTryAgain') || 'Invalid password. Please try again.');
      }
    } catch (err) {
      logger.error({
        message: '[SessionEvictedModal] Re-authentication failed',
        context: { error: err instanceof Error ? err.message : 'Unknown error' },
      });
      setError(t('signInError') || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: 99999 }}
        onClose={() => {
          // Non-dismissible — user must sign in or sign out
        }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
            style={{ zIndex: 99998 }}
          />
        </TransitionChild>

        <div className="fixed inset-0" style={{ zIndex: 99999 }}>
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-amber-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        {t('yourSessionHasEnded') || 'Your session has ended'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {t('sessionEvictedDescription') ||
                          'A new device signed into your account, so this session was automatically ended.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sign-in form */}
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                  <p className="text-sm text-gray-500">
                    {t('signInToResume') || 'Sign in to resume where you left off.'}
                  </p>

                  {/* Email locked to the original user */}
                  <FormField
                    name="email"
                    type="email"
                    label={t('email') || 'Email'}
                    value={storedEmail}
                    onChange={() => {}}
                  />

                  <FormField
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label={t('password') || 'Password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t('enterYourPassword') || 'Enter your password'}
                    iconButton={
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                            />
                          </svg>
                        )}
                      </button>
                    }
                  />

                  {error && (
                    <div className="rounded-md bg-red-50 p-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 pt-1">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? t('signingIn') || 'Signing in...' : t('signIn') || 'Sign In'}
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                      onClick={onSignOut}
                      disabled={isSubmitting}
                    >
                      {t('signOut') || 'Sign Out'}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
